import { MutableRefObject, useCallback, useMemo, useRef, useState } from "react"
import { DateTime, Duration } from "luxon"
import { Box, Divider, Skeleton, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveServiceCenterId } from "../../../../../context/contextSelectors"
import { useDateTime } from "../../../../../localization/useDateTime"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { FormCard } from "../../../../../../shared/components/forms/FormCard"
import { DashboardCardFullSizeSkeleton } from "../../../../../dashboards/components/DashboardCardSkeleton"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { BookingSummaryEntry } from "../../statisticsTypes"
import { useExportBookingsDuration } from "../../hooks/useExportBookingsDuration"

import { HeroStat, HeroStats } from "../resourceDashboard/HeroStats"
import { ResourceUsageBarList } from "../resourceDashboard/ResourceUsageBarList"
import { ResourceTypeSelectionFilter } from "../resourceDashboard/ResourceTypeSelectionFilter"
import {
    ResourceUsageSection,
    ResourceUsageSectionHandle,
    UsageColumn,
} from "../resourceDashboard/ResourceUsageSection"
import {
    ALL_RESOURCE_TYPES,
    DASHBOARD_RESOURCE_TYPES,
    ResourceTypeSelection,
    isResourceType,
} from "../resourceDashboard/resourceDashboardTypes"
import { sumNumberOfBookings, useBookingDashboardData, weightedAverageDuration } from "./useBookingDashboardData"

// Bookings use a single accent colour for every bar (unlike occupancy's status colours).
const BOOKING_BAR_COLOR = tailwindPalette.indigo[600]

const useStyles = makeStyles()((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        gap: "1.875rem",
    },
    filtersCard: {
        padding: "1.5rem",
    },
    filtersRow: {
        display: "flex",
        gap: "1rem",
        alignItems: "flex-end",
        flexWrap: "wrap",
    },
    filtersSpacer: {
        flex: 1,
    },
    summaryInner: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
        width: "100%",
    },
    sections: {
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
    },
    name: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    numeric: {
        fontVariantNumeric: "tabular-nums",
        color: theme.palette.tailwind.slate[700],
    },
    skeletonName: {
        width: "60%",
    },
    skeletonValue: {
        width: "40%",
    },
    chartTitle: {
        color: theme.palette.tailwind.slate[800],
    },
    emptyMessage: {
        color: theme.palette.tailwind.slate[500],
        textAlign: "center",
        padding: "2rem 0",
    },
}))

export function BookingDuration() {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const serviceCenterId = useAppSelector(selectEffectiveServiceCenterId)

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{ startDate: DateTime; endDate: DateTime }>({ startDate, endDate })
    const [selection, setSelection] = useState<ResourceTypeSelection>(ALL_RESOURCE_TYPES)

    const { resultsByType, combinedEntries, presentTypes, summary, isInitialLoading } = useBookingDashboardData({
        serviceCenterId: serviceCenterId ?? 0,
        startDate: dateState.startDate.toISODate(),
        endDate: dateState.endDate.toISODate(),
    })

    const visibleTypes = useMemo<ReadonlyArray<ResourceType>>(
        () => (isResourceType(selection) ? [selection] : presentTypes),
        [selection, presentTypes],
    )
    const visibleTypeSet = useMemo(() => new Set(visibleTypes), [visibleTypes])
    const visibleEntries = useMemo(
        () => combinedEntries.filter((entry) => visibleTypeSet.has(entry.resourceType)),
        [combinedEntries, visibleTypeSet],
    )

    const sectionRefByType = useRef<Record<ResourceType, MutableRefObject<ResourceUsageSectionHandle | null>>>({
        [ResourceType.ROOM]: { current: null },
        [ResourceType.MOBILE_AV]: { current: null },
        [ResourceType.CAR]: { current: null },
    })

    const handleResourceClick = useCallback((resourceId: number, resourceType: ResourceType) => {
        sectionRefByType.current[resourceType].current?.scrollToResource(resourceId)
    }, [])

    const exportBookingDurations = useExportBookingsDuration({
        data: useMemo(() => visibleEntries.slice(), [visibleEntries]),
        startDate: dateState.startDate,
        endDate: dateState.endDate,
    })

    const getDurationMinutes = useCallback(
        (entry: BookingSummaryEntry) => dateTimeFactory.fromDurationString(entry.averageDuration).as("minutes"),
        [dateTimeFactory],
    )
    const formatDurationMinutes = useCallback(
        (minutes: number) =>
            dateTimeFactory.formatDuration(
                Duration.fromObject({ minutes: Math.round(minutes) }).shiftTo("hours", "minutes"),
            ),
        [dateTimeFactory],
    )

    const maxDurationMinutes = useMemo(
        () => visibleEntries.reduce((max, entry) => Math.max(max, getDurationMinutes(entry)), 0),
        [visibleEntries, getDurationMinutes],
    )

    const columns = useMemo<ReadonlyArray<UsageColumn<BookingSummaryEntry>>>(
        () => [
            {
                id: "resource",
                headerId: "service-center-reports-statistic-occupancy-table-resource-title",
                headerFallback: "Resource",
                gridTrack: "minmax(8rem, 2fr)",
                renderCell: (entry) => (
                    <Typography variant="body1" className={classes.name}>
                        {entry.resource.displayName}
                    </Typography>
                ),
                renderSkeleton: () => <Skeleton variant="text" animation="wave" className={classes.skeletonName} />,
            },
            {
                id: "number-of-bookings",
                headerId: "service-center-reports-statistic-booking-duration-table-number-of-bookings-title",
                headerFallback: "Bookings",
                gridTrack: "minmax(6rem, 1fr)",
                align: "end",
                renderCell: (entry) => (
                    <Typography variant="body1" className={classes.numeric}>
                        {entry.numberOfBookings}
                    </Typography>
                ),
                renderSkeleton: () => <Skeleton variant="text" animation="wave" className={classes.skeletonValue} />,
            },
            {
                id: "average-duration",
                headerId: "service-center-reports-statistic-booking-duration-table-average-duration-title",
                headerFallback: "Average duration",
                gridTrack: "minmax(7rem, 1fr)",
                align: "end",
                renderCell: (entry) => (
                    <Typography variant="body1" className={classes.numeric}>
                        {dateTimeFactory.formatDuration(dateTimeFactory.fromDurationString(entry.averageDuration))}
                    </Typography>
                ),
                renderSkeleton: () => <Skeleton variant="text" animation="wave" className={classes.skeletonValue} />,
            },
        ],
        [classes, dateTimeFactory],
    )

    const heroStats = useMemo<ReadonlyArray<HeroStat>>(() => {
        const overview = isResourceType(selection) ? undefined : summary
        const averageDuration = overview?.averageDuration
            ? dateTimeFactory.fromDurationString(overview.averageDuration)
            : weightedAverageDuration(visibleEntries)
        const totalBookings = overview?.numberOfBookings ?? sumNumberOfBookings(visibleEntries)

        return [
            {
                id: "total-bookings",
                value: totalBookings,
                labelId: "service-center-reports-bookings-dashboard-hero-total-bookings-label",
                labelFallback: "Total bookings",
            },
            {
                id: "average-duration",
                value: dateTimeFactory.formatDuration(averageDuration),
                labelId: "service-center-reports-bookings-dashboard-hero-average-duration-label",
                labelFallback: "Average booking duration",
            },
        ]
    }, [selection, summary, visibleEntries, dateTimeFactory])

    const summaryContent = useMemo(() => {
        if (isInitialLoading) {
            return <DashboardCardFullSizeSkeleton />
        }

        const chart =
            visibleEntries.length > 0 ? (
                <ResourceUsageBarList
                    entries={visibleEntries}
                    getValue={(entry) => getDurationMinutes(entry)}
                    axisMax={maxDurationMinutes}
                    getColor={() => BOOKING_BAR_COLOR}
                    formatValue={formatDurationMinutes}
                    onResourceClick={handleResourceClick}
                />
            ) : (
                <LocalizedStrict id="service-center-reports-bookings-dashboard-chart-empty">
                    <Typography variant="body2" className={classes.emptyMessage}>
                        No bookings for this period.
                    </Typography>
                </LocalizedStrict>
            )

        return (
            <Box className={classes.summaryInner}>
                <LocalizedStrict id="service-center-reports-bookings-dashboard-chart-title">
                    <Typography variant="h6" className={classes.chartTitle}>
                        Average duration by resource
                    </Typography>
                </LocalizedStrict>
                {chart}
                <Divider />
                <HeroStats stats={heroStats} />
            </Box>
        )
    }, [
        isInitialLoading,
        visibleEntries,
        heroStats,
        getDurationMinutes,
        formatDurationMinutes,
        maxDurationMinutes,
        handleResourceClick,
        classes,
    ])

    const sectionsContent = useMemo(() => {
        if (!isInitialLoading && visibleTypes.length === 0) {
            return (
                <FormCard>
                    <LocalizedStrict id="service-center-reports-bookings-dashboard-empty">
                        <Typography variant="body2" className={classes.emptyMessage}>
                            No resources found for this service center.
                        </Typography>
                    </LocalizedStrict>
                </FormCard>
            )
        }

        let typesToRender = visibleTypes
        if (isInitialLoading) {
            typesToRender = isResourceType(selection) ? [selection] : DASHBOARD_RESOURCE_TYPES
        }

        return typesToRender.map((resourceType) => (
            <ResourceUsageSection
                key={resourceType}
                resourceType={resourceType}
                queryResult={resultsByType[resourceType]}
                columns={columns}
                handleRef={sectionRefByType.current[resourceType]}
                emptyMessageId="service-center-reports-bookings-dashboard-empty"
                emptyMessageFallback="No resources found for this service center."
            />
        ))
    }, [isInitialLoading, selection, visibleTypes, resultsByType, columns, classes])

    return (
        <Box className={classes.root}>
            <FormCard className={classes.filtersCard}>
                <Box className={classes.filtersRow}>
                    <LocalizedStrict
                        id="service-center-reports-statistic-occupancy-table-date-range-filter"
                        attrs={{ label: true }}
                    >
                        <StandardDateRangeFilter
                            value={dateState}
                            onChange={setDateState}
                            label="Date range"
                            variant="labeled"
                        />
                    </LocalizedStrict>
                    <ResourceTypeSelectionFilter value={selection} onChange={setSelection} />
                    <Box className={classes.filtersSpacer} />
                    <StandardExportButton onClick={exportBookingDurations} />
                </Box>
            </FormCard>

            <FormCard>{summaryContent}</FormCard>

            <Box className={classes.sections}>{sectionsContent}</Box>
        </Box>
    )
}
